/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useState, useRef, useEffect, useCallback } from 'react'
import Flatpickr from 'react-flatpickr'
import 'flatpickr/dist/themes/material_blue.css'
import { LanguageHelp, getConfig } from '../../../helpers/LanguageHelper'
import { KTSVG } from '../../../helpers'
import { Turkish } from "flatpickr/dist/l10n/tr"
import { German } from "flatpickr/dist/l10n/de"
import { Russian } from "flatpickr/dist/l10n/ru"
import { Arabic } from "flatpickr/dist/l10n/ar"
import { english } from "flatpickr/dist/l10n/default"

type Props = {
  className?: string
  className_Label?: string
  functionSubmit?: any
  selectedDate?: string
  autoClose?: boolean
  caretUsage?: boolean
}

const DropdownFilterDate: FC<Props> = ({
  className = '',
  className_Label = 'btn btn-light btn-active-light-primary btn-sm',
  functionSubmit,
  selectedDate = LanguageHelp('Last 30 days'),
  autoClose = true,
  caretUsage = true,
}) => {
  const lang = getConfig()
  const localeMap: Record<string, any> = {
    tr: Turkish,
    de: German,
    ru: Russian,
    ar: Arabic,
    en: english,
  }
  const locale = localeMap[lang.selectedLang] || english

  const [label, setLabel] = useState<string>(selectedDate)
  const [pickerKey, setPickerKey] = useState<number>(0)
  const [menuRefreshKey, setMenuRefreshKey] = useState<number>(0)
  const [menuCheckKey, setMenuCheckKey] = useState<number>(0)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLAnchorElement | null>(null)
  const menuCheckRef = useRef<NodeJS.Timeout | null>(null)

  // Update label when selectedDate prop changes
  useEffect(() => {
    // Check if selectedDate is API format (YYYY-MM-DD|YYYY-MM-DD)
    const apiFormatRegex = /^\d{4}-\d{2}-\d{2}\|\d{4}-\d{2}-\d{2}$/
    if (apiFormatRegex.test(selectedDate)) {
      const [startStr, endStr] = selectedDate.split('|')
      const start = new Date(startStr)
      const end = new Date(endStr)
      setLabel(formatLabel(start, end))
    } else {
      setLabel(selectedDate)
    }
  }, [selectedDate])

  // Update time when minute changes (only when menu is open)
  useEffect(() => {
    const checkMenuOpen = () => {
      const menuElement = menuRef.current
      if (!menuElement) return

      const isOpen = menuElement.classList.contains('show')
      if (!isOpen) {
        if (menuCheckRef.current) clearInterval(menuCheckRef.current)
        return
      }

      let lastMinute = new Date().getMinutes()
      const interval = setInterval(() => {
        const currentMinute = new Date().getMinutes()
        if (currentMinute !== lastMinute) {
          lastMinute = currentMinute
          setMenuRefreshKey(prev => prev + 1)
        }
      }, 3000)

      menuCheckRef.current = interval
    }

    checkMenuOpen()
    return () => {
      if (menuCheckRef.current) clearInterval(menuCheckRef.current)
    }
  }, [menuCheckKey])

  // Close menu (Metronic)
  const closeMenu = () => {
    try {
      // @ts-ignore
      if (window?.KTMenu?.hideAll) {
        // @ts-ignore
        window.KTMenu.hideAll()
        return
      }
    } catch (e) {
      // continue to next method
    }

    if (triggerRef.current) {
      triggerRef.current.click()
    }
  }

  // Reset Flatpickr after menu closes
  const resetPicker = () => {
    setTimeout(() => setPickerKey(prev => prev + 1), 300)
  }

  // Backend standard: YYYY-MM-DD|YYYY-MM-DD
  const formatToApi = (start: Date, end: Date) => {
    const f = (d: Date) => {
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
    return `${f(start)}|${f(end)}`
  }

  // Locale format for label - memoized to avoid duplication
  const formatLabel = useCallback((start: Date, end: Date) => {
    const formatDate = (date: Date) => {
      if (lang.selectedLang === 'tr' || lang.selectedLang === 'de' || lang.selectedLang === 'ru') {
        const day = String(date.getDate()).padStart(2, '0')
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const year = date.getFullYear()
        return `${day}.${month}.${year}`
      }
      if (lang.selectedLang === 'ar') {
        return date.toLocaleDateString('ar', { day: '2-digit', month: '2-digit', year: 'numeric' })
      }
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const month = months[date.getMonth()]
      const day = date.getDate()
      const year = date.getFullYear()
      return `${month} ${day}, ${year}`
    }

    const s = formatDate(start)
    const e = formatDate(end)
    return `${s} - ${e}`
  }, [lang.selectedLang])

  // Preset: single click + menu closes
  const handlePreset = (preset: string) => {
    setLabel(preset)
    functionSubmit?.(preset, 'date')
    closeMenu()
    resetPicker()
  }

  // When range is selected (after 2 dates are completed)
  const handleRangeChange = (dates: Date[]) => {
    if (dates.length !== 2) return

    const [start, end] = dates

    const rangeString = formatToApi(start, end)
    const rangeLabel = formatLabel(start, end)

    functionSubmit?.(rangeString, 'date')
    setLabel(rangeLabel)
    closeMenu()
    resetPicker()
  }

  // Get dynamic date-time range
  const getDateTimeRange = (days: number) => {
    const now = new Date()
    const pastDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000)

    const formatTime = (date: Date) => {
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${hours}:${minutes}`
    }

    return `(${formatTime(pastDate)} - ${formatTime(now)})`
  }

  const stopIfNeeded = (e: React.MouseEvent) => {
    if (!autoClose) {
      e.stopPropagation()
    }
  }

  return (
    <div className={className}>
      <a
        ref={triggerRef}
        href='#'
        className={className_Label}
        data-kt-menu-trigger='click'
        data-kt-menu-placement='bottom-end'
        onClick={() => {
          setMenuCheckKey(prev => prev + 1)
          setMenuRefreshKey(prev => prev + 1)
        }}
      >
        <div style={{ display: 'unset', margin: 'auto' }}>{label}</div>
        {caretUsage && <KTSVG path='/media/icons/duotune/arrows/arr072.svg' className='svg-icon-5 m-0' />}
      </a>

      {/* begin::Menu */}
      <div
        ref={menuRef}
        className='menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary fw-bold fs-7 w-250px py-4'
        data-kt-menu='true'
        onClick={(e) => stopIfNeeded(e)}
        onMouseDown={(e) => stopIfNeeded(e)}
      >
        {/* Presets */}
        <div className='menu-item px-3'>
          <a className='menu-link px-3' onClick={() => handlePreset(LanguageHelp('Today'))}>
            {LanguageHelp('Today')}
          </a>
        </div>
        <div className='menu-item px-3'>
          <a className='menu-link px-3' onClick={() => handlePreset(LanguageHelp('Last 7 days'))}>
            {LanguageHelp('Last 7 days')}{' '}<span className='text-muted fs-8 ms-1'>{getDateTimeRange(7)}</span>
          </a>
        </div>
        <div className='menu-item px-3'>
          <a className='menu-link px-3' onClick={() => handlePreset(LanguageHelp('Last 30 days'))}>
            {LanguageHelp('Last 30 days')}{' '}<span className='text-muted fs-8 ms-1'>{getDateTimeRange(30)}</span>
          </a>
        </div>
        <div className='menu-item px-3'>
          <a className='menu-link px-3' onClick={() => handlePreset(LanguageHelp('Last 1 year'))}>
            {LanguageHelp('Last 1 year')}{' '}<span className='text-muted fs-8 ms-1'>{getDateTimeRange(365)}</span>
          </a>
        </div>
        <div className='menu-item px-3'>
          <a className='menu-link px-3' onClick={() => handlePreset(LanguageHelp('All time'))}>
            {LanguageHelp('All time')}
          </a>
        </div>

        <div className='separator my-2'></div>

        {/* Custom Range */}
        <div className='px-3 pb-2 text-muted fw-semibold'>
          {LanguageHelp('Custom Range')}
        </div>

        {/* Single Date Range Picker */}
        <div className='px-3 mb-2'>
          <Flatpickr
            key={pickerKey}
            className='form-control form-control-sm'
            options={{
              mode: 'range',
              dateFormat: 'Y-m-d', // Fixed for backend
              altInput: true, // Nice display for user
              altFormat: (lang.selectedLang === 'tr' || lang.selectedLang === 'de' || lang.selectedLang === 'ru') ? 'd.m.Y' : (lang.selectedLang === 'ar' ? 'd/m/Y' : 'F j, Y'),
              locale: locale,
              allowInput: false
            }}
            placeholder={LanguageHelp('Select Date Range')}
            onChange={handleRangeChange}
          />
        </div>
      </div>
      {/* end::Menu */}
    </div>
  )
}

export { DropdownFilterDate }
